import { type Configuration, PublicClientApplication, LogLevel } from '@azure/msal-browser';

const CLIENT_ID = process.env.AZURE_AD_CLIENT_ID || '{clientId}';
const TENANT_ID = process.env.AZURE_AD_TENANT_ID || '{tenantId}';
const REDIRECT_URI = `${window.location.origin}/login/callback`;
const API_SCOPE = process.env.AZURE_AD_API_SCOPE || 'api://duro.care/duro-provider-local/access_as_user';

// MSAL Configuration
export const msalConfig: Configuration = {
	auth: {
		clientId: CLIENT_ID,
		authority: `https://login.microsoftonline.com/${TENANT_ID}`,
		redirectUri: REDIRECT_URI,
		postLogoutRedirectUri: window.location.origin,
		// false: always process the auth code on /login/callback and let our own
		// sessionStorage redirectUri bookkeeping decide where to land, instead of
		// MSAL bouncing back to whatever page initiated the login.
		navigateToLoginRequestUrl: false
	},
	cache: {
		cacheLocation: 'localStorage', // This configures where your cache will be stored
		storeAuthStateInCookie: false // Set to true for IE 11 or Edge
	},
	system: {
		loggerOptions: {
			loggerCallback: (level, message, containsPii) => {
				if (containsPii) {
					return;
				}
				switch (level) {
					case LogLevel.Error:
						console.error(message);
						return;
					case LogLevel.Warning:
						console.warn(message);
						return;
					default:
						return;
				}
			}
		}
	}
};

// Scopes for API access
export const loginRequest = {
	scopes: ['openid', 'profile', 'email', API_SCOPE]
};

// Scopes for API calls (without openid/profile as those are only for login)
export const apiRequest = {
	scopes: [API_SCOPE]
};

// Create the MSAL instance
export const msalInstance = new PublicClientApplication(msalConfig);

// Initialize MSAL. Cached so the router loader (which runs at router-creation time,
// before App's effect fires) and App share a single initialization.
let initPromise: Promise<void> | null = null;

export const initializeMsal = () => {
	if (!initPromise) {
		initPromise = (async () => {
			await msalInstance.initialize();

			// Handle redirect promise
			try {
				const response = await msalInstance.handleRedirectPromise();
				if (response?.account) {
					msalInstance.setActiveAccount(response.account);
				}

				// Ensure an active account is set whenever one exists. Covers both a returning
				// user (no redirect response) and a redirect response that lacks an account —
				// without this, useIsAuthenticated() can stay false and the login callback hangs.
				if (!msalInstance.getActiveAccount()) {
					const accounts = msalInstance.getAllAccounts();
					if (accounts.length > 0) {
						msalInstance.setActiveAccount(accounts[0]!);
					}
				}
			} catch (error) {
				console.error('Error handling redirect:', error);
			}
		})();
	}
	return initPromise;
};
